<template>
  <q-popup-proxy >
    <q-list style="background-color: white; min-width:300px">
      <q-item clickable v-for="cart in accountCarts" :key="cart.id" :active="cart.id === activeCartId">
        <q-item-section v-close-popup @click="changeActiveCart(cart.id)">{{cart.name}}</q-item-section>
        <q-item-section side>
          <div class="q-gutter-none ">
            <q-btn icon="edit" size="sm" dense flat class="q-ma-none" @click="editCartName(cart.id)" />
            <q-btn icon="file_copy" size="sm" dense flat class="q-ma-none" @click="copyCart(cart.id)" />
            <q-btn icon="delete" size="sm" dense flat class="q-ma-none" @click="deleteCartWithConfirmation(cart.id)" />
          </div>
        </q-item-section>
      </q-item>
      <q-item clickable :active="true" v-if="activeCartIsOwnedByOtherAccount">
        <q-item-section v-close-popup>{{(activeCart||{}).name}} <strong>({{((activeCart||{}).account||{}).companyName}}'s cart)</strong></q-item-section>
      </q-item>
      <q-item clickable v-close-popup @click="createNewCartWithName">
        <q-item-section avatar>
          <q-icon name="add" />
        </q-item-section>
        <q-item-section>Add New</q-item-section>
      </q-item>
    </q-list>
  </q-popup-proxy>
</template>

<script>
import gql from 'graphql-tag'
import moment from 'moment'
import { properCase } from '../util/string'
import store from "../store";


export default {
  name: 'EditCartMenu',
  props: ['storeType'],
  apollo: {
    accountCarts () {
      return {
        query: gql`query accountCarts {
            accountCarts {
                id,
                name,
                cartType,
                items {
                  id,
                  skuId,
                  qty,
                  sku {
                      id
                  }
                },
                createdAt
            }
        }
        `,
        fetchPolicy: 'network-only',
        update (data) {         
          // cart.cartType is like 'StockCart', 'ContainerCart' and this.storeType is like 'stock', 'container'
          const accountCarts = data.accountCarts?.filter(cart => cart.cartType.toLowerCase() === this.storeType.toLowerCase() + 'cart')
          if (accountCarts?.length) {
            return accountCarts
          }
          return []
        },
        async result ({ data }) {          
          const accountCarts = data.accountCarts?.filter(cart => cart.cartType.toLowerCase() === this.storeType.toLowerCase() + 'cart')
          if (this.currentAccount && !accountCarts.length) {           
            await this.$store.dispatch('cart/createNewActiveCart', {
              input: { name: `${properCase(this.storeType)} Cart ${moment().format('DD/MM/YY')}` },
              storeType: this.storeType
            })
            await this.$apollo.queries.accountCarts.refetch()
          }
        },
        // skip: () => !this.currentAccount 
        skip: () => !this.currentAccount
      }
    },
    activeCart () {
      return {
        query: gql`
          query activeCart ($id: ID!) {
            cart(id: $id) {
              id,
              name,
              notes,
              account {
                companyName
              }
              items {
                id
                skuId
                qty,
                sku {
                    id,
                    stock,
                    price,
                    attributes,
                    options,
                    product {
                        id,
                        name,
                        images {
                          url
                          name
                        },
                        attributes,
                        urlSlug,
                    }
                }
              }
            }
          }
        `,
        variables () {
          return {
            id: this.activeCartId
          }
        },
        update: data => data.cart,
        skip: () => !this.activeCartId,
        context: {
          headers: {
            uuid: this.currentUUID
          }
        },
        error (error) {
          if (error.message === `GraphQL error: Cart with id ${this.activeCartId} does not exist.`) {
          }
        }
      }
    }
  },
  computed: {
    currentUUID () {
      return this.$store.state.auth.currentUUID
    },
    currentAccount () {
      return this.$store.state.auth.currentAccount
    },
    activeCartId () {
      if (this.storeType === 'stock') {
        return this.currentAccount?.activeCartId
      } else if (this.storeType === 'container') {
        return this.currentAccount?.activeContainerCartId
      } else if (this.storeType === 'retail') {
        if(!!this.currentAccount){
          return this.currentAccount?.activeRetailCartId
        }else{
          return this.$store.state.auth.currentGuest.activeRetailCartId;
        }
      }      
      return null
    },
    activeCartIsOwnedByOtherAccount () {
      return !this.accountCarts?.find?.(cart => cart.id === this.activeCartId)
    }
  },
  methods: {
    redirectToStore () {
      if (this.storeType == 'stock') this.$router.push(`/stock`)
      else if (this.storeType == 'container') this.$router.push(`/container`)
      else if (this.storeType == 'retail') this.$router.push(`/retail`)
    },
    createNewCartWithName () {
      this.$q.dialog({
        title: 'Name Your New Cart',
        cancel: true,
        prompt: {
          model: `${properCase(this.storeType)} Cart ${moment().format('DD/MM/YY')}`,
          type: 'text'
        }
      }).onOk(async (newName) => {
        await this.$store.dispatch('cart/createNewActiveCart', { input: { name: newName }, storeType: this.storeType })
        await this.$store.dispatch('cart/init');
        await this.$apollo.queries.accountCarts.refetch();
        this.redirectToStore();
      })
    },
    editCartName (cartId) {
      const oldName = this.accountCarts.find(cart => cart.id === cartId).name
      this.$q.dialog({
        title: 'Change Cart Name',
        cancel: true,
        prompt: {
          model: oldName,
          type: 'text' // optional
        }
      }).onOk(async (newName) => {
        try {
          await this.editCart(cartId, { name: newName })
          // eslint-disable-next-line no-undef
          analytics.track('Rename Cart', {
            cartId: this.cartId,
            oldName,
            newName
          })
        } catch (err) {
        }
      })
    },
    async copyCart (cartId) {
      try {
        const result = await this.$apollo.query({
          query: gql`
            query cart ($id: ID!) {
              cart(id: $id) {
                id,
                createdAt,
                name,
                notes,
                createdAt,
                updatedAt,
                items {
                  id,
                  skuId,
                  notes,
                  qty
                }
              }
            }
          `,
          variables: {
            id: cartId
          },
          context: {
            headers: {
              uuid: this.currentUUID
            }
          },
          fetchPolicy: 'network-only'
        })
        const cartToCopy = result.data.cart
        const newCart = {
          name: cartToCopy.name + ' (Copy)',
          notes: cartToCopy.notes,
          items: []
        }
        for (const item of cartToCopy.items) {
          newCart.items.push({
            skuId: item.skuId,
            notes: item.notes,
            qty: item.qty
          })
        }
        await this.$store.dispatch('cart/createNewActiveCart', { input: newCart, storeType: this.storeType })
        await this.$apollo.queries.accountCarts.refetch()
      } catch (err) {
      }
    },
    deleteCartWithConfirmation (cartId) {
      this.$q.dialog({
        title: `Delete ${this.accountCarts.find(cart => cart.id === cartId).name}`,
        message: `Are you sure you want to delete this cart?`,
        cancel: true,
        persistent: true
      }).onOk(() => {
        this.deleteCart(cartId)
      })
    },
    async changeActiveCart (activeCartId) {
      if (this.activeCartId === activeCartId) return // Don't update if it didn't change
      if (this.storeType === 'stock') {
        await this.$store.dispatch('auth/updateCurrentAccount', { activeCartId })
      } else if (this.storeType === 'container') {
        await this.$store.dispatch('auth/updateCurrentAccount', { activeContainerCartId: activeCartId })
      } else if (this.storeType === 'retail') {
        await this.$store.dispatch('auth/updateCurrentAccount', { activeRetailCartId: activeCartId })
      }
      await this.$store.dispatch('cart/init');
      this.redirectToStore();
      this.$q.notify({
        message: `Active ${properCase(this.storeType)} cart changed to ${this.accountCarts.find(cart => cart.id === activeCartId).name}`,
        icon: 'done',
        color: 'grey'
      })
      
    },
    async deleteCart (cartId) {
      try {
        const cartData = Object.assign({}, this.accountCarts.find(cart => cart.id === cartId))
        await this.$apollo.mutate({
          mutation: gql`mutation deleteCart($cartId: ID!) {
            deleteCart(id: $cartId) {
              id
            }
          }`,
          variables: {
            cartId
          }
        })
        // eslint-disable-next-line no-undef
        analytics.track('Delete Cart', {
          cartId: this.cartId,
          cartData: cartData
        })
        await this.$apollo.queries.accountCarts.refetch()
        if (this.accountCarts.length) {
          await this.changeActiveCart(this.accountCarts[0].id)
        }
      } catch (error) {
        this.$q.notify({
          message: 'Could not delete cart!',
          color: 'negative',
          icon: 'report_problem'
        })
      }
    },
    async editCart (cartId, changes) {
      await this.$apollo.mutate({
        mutation: gql`
          mutation editCart($cartId: ID!, $input: CartInput!) {
            editCart(id: $cartId, input: $input) {
              id,
              name,
              notes,
              items {
                id,
                skuId,
                qty,
                sku {
                  id,
                  stock,
                  price,
                  attributes,
                  options,
                  product {
                    id,
                    name,
                    images {
                      url
                      name
                    },
                    attributes,
                    urlSlug,
                  }
                }
              }
            }
          }`,
        variables: {
          cartId: cartId,
          input: changes
        },
        context: {
          headers: {
            uuid: this.currentUUID
          }
        }        
      }).catch(err => {
        console.log(err)
        this.$q.notify({
          message: 'Error updating!',
          color: 'negative',
          icon: 'report_problem'
        })
        throw err
      })
    }
  }
}
</script>

<style scoped>

</style>
